// patient-history.js — CLIENT-SIDE shaping, raw FHIR -> the Visit[] DTO the
// PatientTimelineDrawer (patient-timeline.jsx) renders. This is the ONLINE-PRIMARY
// port of the server's listPatientVisitHistory (server/opd/state-machine.mjs:
// 1413-1522): one Visit per Encounter, newest-first, each carrying the attending
// doctor + department + the derived consult card (chief complaint / diagnosis /
// meds / investigations / follow-up) + this-visit reports.
//
// This is the PURE shaper (no MedplumClient) — medplum-history.js does the one
// patient-wide graph fetch and calls shapePatientHistory(graph) below. Kept pure
// so a Node test can feed it a synthetic bundle.
//
// WHAT THE REDESIGN CHANGES vs. the server builder (same rationale as
// encounter-view.js):
//   • The disk-only `_healther` bags (appointmentSnapshot / referral) and the
//     app-only `_draft` bag do NOT exist on a plain FHIR fetch. The consult draft
//     now rides in a Composition OPD_DRAFT extension (seed convention,
//     seed-demo-queue.mjs:22 + REDESIGN §12); we read that extension and JSON.parse
//     it back into the same `draft` shape the server read off `_draft`.
//   • With `_draft` gone for most seeded/pulled visits, the FHIR-derived card
//     (deriveVisitSummaryFromFhir — Condition / MedicationRequest / ServiceRequest
//     / QuestionnaireResponse) is the PRIMARY source, and the OPD_DRAFT extension
//     is the additive override when a real app-authored consult exists. This is the
//     same "derive the app shape from standalone FHIR" fix the server ships, just
//     with the extension read added on top.
//   • doctor/department are re-derived from the Encounter's own FHIR fields
//     (participant / serviceProvider), the appointmentSnapshot's FHIR equivalents —
//     mirroring shapeQueueRow (shape/queue-row.js) and buildEncounterView.
//
// The returned Visit shape is the EXACT contract from state-machine.mjs:1495-1519
// (fields: encounterId, date, doctor, doctorId, department, source, isOtherDoctor,
//  state, chiefComplaint, diagnosis, icd10, medications[{name,strength,frequency,
//  freqGrid,duration,dose}], investigations[{name}], followup, reports[Report],
//  reportCounts{total,external,thisHospital}, referral{from,fromDoctor,priority}|null),
//  PLUS encounterClass (added — see below): the raw FHIR Encounter.class.code
//  (v3-ActCode, e.g. 'AMB'/'IMP'/'EMER'), or null when the Encounter carries no
//  class. The server builder never read this, and neither did this shaper until
//  now — the UI (patient-timeline.jsx) needs it to tell an out-patient consult
//  apart from an inpatient admission or an ED visit so it can label/link each
//  prior visit honestly instead of always offering "Open this consult" (which
//  used to silently redirect into the ward via the wrongSurfaceTarget safety net
//  in medplum-encounter.js — a real UX bug, not just defense-in-depth).

const OPD_STATE = 'https://healther.io/opd/state';
// The consult-draft Composition FHIR contract (redesign): draft JSON in a single
// OPD_DRAFT extension. Same URL the encounter-view shaper + seed use.
const OPD_DRAFT = 'https://healther.io/opd/draft';

const ext = (res, url) => ((res && res.extension) || []).find((e) => e.url === url);
const strip = (s) => String(s || '').replace(/<[^>]*>/g, '').trim();

// ── helpers ported verbatim from state-machine.mjs so the shape is identical ──

// Free-text frequency → the doctor "morning-noon-night" grid ("1-0-1" = BD).
// (state-machine.mjs:1216-1229 freqToGrid — ported byte-for-byte.)
function freqToGrid(raw) {
  const f = String(raw || '').toLowerCase().replace(/\./g, '').replace(/\s+/g, ' ').trim();
  if (!f) return '';
  const g = f.match(/(\d+)\s*-\s*(\d+)\s*-\s*(\d+)(?:\s*-\s*(\d+))?/); // already a grid
  if (g) return g[0].replace(/\s+/g, '');
  if (/\b(sos|prn|stat|as needed|when required|if needed)\b/.test(f)) return 'SOS';
  if (/\b(qid|qds|four times)\b/.test(f)) return '1-1-1-1';
  if (/\b(tds|tid|thrice|three times)\b/.test(f)) return '1-1-1';
  if (/\b(bd|bid|twice|two times)\b/.test(f)) return '1-0-1';
  if (/\b(at night|bedtime|before bed|\bhs\b|once at night)\b/.test(f)) return '0-0-1';
  if (/\b(afternoon|noon|before lunch|after lunch|midday)\b/.test(f)) return '0-1-0';
  if (/\b(morning|breakfast|every morning|\bam\b|once daily|once a day|\bod\b|\bqd\b|daily)\b/.test(f)) return '1-0-0';
  return '';
}

// Collapse repeated meds by normalized drug NAME (strip strength + punctuation)
// so a visit shows each drug ONCE. (state-machine.mjs:1150-1176 dedupeMeds.)
function dedupeMeds(list) {
  const out = [];
  const byKey = new Map();
  for (const m of (list || [])) {
    const base = String(m.name || '').trim().toLowerCase()
      .replace(/\b\d+(?:\.\d+)?\s*(?:mg|mcg|ml|gm?|iu|units?|%)\b/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
    const key = base || `__unnamed-${out.length}`;
    const prev = byKey.get(key);
    if (!prev) { byKey.set(key, m); out.push(m); continue; }
    if ((m.name || '').length > (prev.name || '').length) prev.name = m.name;
    prev.strength = prev.strength || m.strength || '';
    prev.freqGrid = prev.freqGrid || m.freqGrid || '';
    prev.frequency = prev.frequency || m.frequency || '';
    prev.duration = prev.duration || m.duration || '';
    prev.dose = [prev.strength, prev.freqGrid || prev.frequency, prev.duration].filter(Boolean).join(' · ');
  }
  return out;
}

// image | audio | pdf | video | file. (state-machine.mjs:1105-1113 fileKindFromMime.)
function fileKindFromMime(mime) {
  const m = String(mime || '').toLowerCase();
  if (!m) return 'file';
  if (m.startsWith('image/')) return 'image';
  if (m.startsWith('audio/')) return 'audio';
  if (m === 'application/pdf' || m.includes('pdf')) return 'pdf';
  if (m.startsWith('video/')) return 'video';
  return 'file';
}

// One DocumentReference → the Report shape the drawer renders. (state-machine.mjs:
// 1075-1102 reportFromDocRef.) The disk-only `_healther` provenance bag doesn't
// exist on a plain FHIR fetch; where it's absent the classification falls back to
// the DocumentReference.type code, which is exactly what the server does when
// _healther is missing. External = patient-upload / brought-from-prior.
function reportFromDocRef(r) {
  const att = (r.content && r.content[0] && r.content[0].attachment) || {};
  const code = (r.type && r.type.coding && r.type.coding[0] && r.type.coding[0].code) || 'document';
  const h = r._healther || {};
  const origin = h.origin || null; // e.g. 'external-prior', 'patient-upload'
  const external = code === 'patient-upload'
    || code === 'external'
    || origin === 'external-prior'
    || origin === 'patient-upload'
    || origin === 'external';
  return {
    id: r.id,
    title: att.title || code,
    url: att.url || null,
    contentType: att.contentType || null,
    fileType: fileKindFromMime(att.contentType),
    docType: code,
    date: (r.meta && r.meta.lastUpdated) || r.date || null,
    source: external ? 'external' : 'this-hospital',
    origin,
    attachedBy: h.attachedBy || null,
    facility: h.facility || null,
    summary: h.summary || null,
    ocr: h.ocr || null,
  };
}

// Attending doctor for an encounter. The disk appointmentSnapshot doesn't exist
// on a FHIR fetch, so we read the FHIR Encounter.participant list directly (its
// snapshot equivalent) — same source shapeQueueRow/buildEncounterView use.
// (state-machine.mjs:1046-1060 doctorForEncounter, FHIR-only branch.)
function doctorForEncounter(enc) {
  const snap = (enc && enc._healther && enc._healther.appointmentSnapshot) || {};
  let doctorId = snap.doctorId || null;
  let doctor = snap.doctorDisplay || '';
  if (!doctorId || !doctor) {
    const indiv = ((enc && enc.participant) || [])
      .map((p) => p.individual)
      .find((i) => i && /^Practitioner\//.test((i && i.reference) || ''));
    if (indiv) {
      if (!doctorId) doctorId = (indiv.reference || '').replace(/^Practitioner\//, '') || null;
      if (!doctor) doctor = indiv.display || '';
    }
  }
  return { doctorId, doctor };
}

// Department for an encounter — snapshot's FHIR equivalent is Encounter.
// serviceProvider.display / serviceType (same as shapeQueueRow). Falls back to
// the disk _healther bags for mirrored rows. (state-machine.mjs:1063-1067.)
function departmentForEncounter(enc) {
  return (enc && enc._healther && enc._healther.appointmentSnapshot && enc._healther.appointmentSnapshot.department)
    || (enc && enc._healther && enc._healther.department)
    || (enc && enc.serviceProvider && enc.serviceProvider.display)
    || (enc && enc.serviceType && enc.serviceType[0] && enc.serviceType[0].text)
    || '';
}

function stateOf(enc) {
  const e = ext(enc, OPD_STATE);
  return (e && e.valueString) || null;
}

// Raw FHIR Encounter.class.code (v3-ActCode: 'AMB'/'IMP'/'EMER'/…), or null when
// the Encounter carries no class Coding at all (legacy/incomplete records). Kept
// as the raw code (not upper-cased here) — consumers that branch on it should
// upper-case, mirroring medplum-encounter.js's wrongSurfaceTarget.
function classCodeOf(enc) {
  return (enc && enc.class && enc.class.code) || null;
}

// Composition kind resolver — matches the server's freshestByKind (type.coding[0]
// .code OR type.text OR the disk _healther.kind). (state-machine.mjs:1027-1032,
// widened to also read type.text since the seed writes visit-summary as type.text.)
function compKind(c) {
  return (c && c._healther && c._healther.kind)
    || (c && c.type && c.type.coding && c.type.coding[0] && c.type.coding[0].code)
    || (c && c.type && c.type.text)
    || '';
}
function freshestByKind(comps, kind) {
  const list = (comps || []).filter((c) => compKind(c) === kind);
  list.sort((a, b) => String((b.date || (b.meta && b.meta.lastUpdated)) || '').localeCompare(String((a.date || (a.meta && a.meta.lastUpdated)) || '')));
  return list[0] || null;
}

// The consult draft: prefer the disk _draft shadow (mirrored rows), else the
// OPD_DRAFT extension JSON (the FHIR-correct home). (encounter-view.js:387-393
// draftJsonFromComposition.)
function draftFromComposition(comp) {
  if (!comp) return {};
  if (comp._draft) return comp._draft;
  const e = ext(comp, OPD_DRAFT);
  if (e && e.valueString) { try { return JSON.parse(e.valueString) || {}; } catch { return {}; } }
  return {};
}

// intake-summary Composition chief_complaint section text. (state-machine.mjs:
// 1034-1038 chiefComplaintOf — the _payload bag becomes the section text.div on a
// FHIR fetch, so read either.)
function chiefComplaintOf(intakeComp) {
  const s = ((intakeComp && intakeComp.section) || []).find((x) => x.code && x.code.coding && x.code.coding[0] && x.code.coding[0].code === 'chief_complaint');
  if (!s) return null;
  const p = s._payload || {};
  const fromPayload = p.text || p.doctor || p.patient || null;
  if (fromPayload && String(fromPayload).trim()) return String(fromPayload).trim();
  const div = s.text && s.text.div ? strip(s.text.div) : '';
  return div || null;
}

// ── deriveVisitSummaryFromFhir — ported from state-machine.mjs:1329-1411 ──────
// Rebuilds a visit card from standalone FHIR children when the app draft left a
// field empty. Pure + additive.
function qrChiefComplaint(qr) {
  const step1 = ((qr && qr.item) || []).find((i) => i && i.linkId === 'step1');
  if (!step1) return null;
  const direct = step1.answer && step1.answer[0] && step1.answer[0].valueString;
  if (direct && direct.trim()) return direct.trim();
  const nested = (((step1.item || []).find((i) => /text$/.test((i && i.linkId) || '')) || {}).answer || [])[0]
    || ((step1.item && step1.item[0] && step1.item[0].answer) || [])[0];
  const v = nested && nested.valueString;
  return (v && v.trim()) ? v.trim() : null;
}
function diagnosisFromCondition(cond) {
  const text = (cond && cond.code && cond.code.text)
    || ((cond && cond.code && cond.code.coding) || []).filter((c) => !/icd-10/i.test((c && c.system) || '')).map((c) => c.display).find(Boolean)
    || null;
  const icd10 = (((cond && cond.code && cond.code.coding) || []).find((c) => /icd-10/i.test((c && c.system) || '')) || {}).code || null;
  return text ? { diagnosis: String(text).trim(), icd10: icd10 || null } : null;
}
function deriveVisitSummaryFromFhir(enc, {
  compositions = [], conditions = [], medRequests = [],
  questionnaireResponses = [], serviceRequests = [],
} = {}) {
  const out = {};

  // 1) Chief complaint — freshest intake QR step1, then a chief_complaint
  //    Composition section.
  const qr = (questionnaireResponses || [])
    .slice()
    .sort((a, b) => String((b.authored || (b.meta && b.meta.lastUpdated)) || '').localeCompare(String((a.authored || (a.meta && a.meta.lastUpdated)) || '')))[0];
  const ccFromQr = qr ? qrChiefComplaint(qr) : null;
  if (ccFromQr) out.chiefComplaint = ccFromQr;
  if (!out.chiefComplaint) {
    for (const comp of (compositions || [])) {
      const sec = ((comp.section || []).find((s) => s.code && s.code.coding && s.code.coding[0] && s.code.coding[0].code === 'chief_complaint'));
      const p = (sec && sec._payload) || {};
      const cc = p.text || p.doctor || p.patient || (sec && sec.text && sec.text.div ? strip(sec.text.div) : null);
      if (cc && String(cc).trim()) { out.chiefComplaint = String(cc).trim(); break; }
    }
  }

  // 2) Diagnosis + ICD-10 — prefer the encounter-diagnosis Condition, else any.
  const dxConds = (conditions || []).filter((c) => c && c.resourceType === 'Condition');
  const encDx = dxConds.find((c) =>
    (c.category || []).some((cat) => (cat.coding || []).some((cd) => cd.code === 'encounter-diagnosis')));
  const dxCond = encDx || dxConds[0] || null;
  const dx = dxCond ? diagnosisFromCondition(dxCond) : null;
  if (dx) { out.diagnosis = dx.diagnosis; if (dx.icd10) out.icd10 = dx.icd10; }

  // 3) Medications — the encounter's MedicationRequest(s).
  const meds = dedupeMeds(
    (medRequests || [])
      .filter((m) => m && m.status !== 'entered-in-error')
      .map((m) => {
        const text = (m.dosageInstruction && m.dosageInstruction[0] && m.dosageInstruction[0].text) || '';
        const grid = freqToGrid(text);
        const durMatch = text.match(/(\d+\s*(?:days?|weeks?|months?))/i);
        const dur = durMatch ? durMatch[1] : undefined;
        return {
          name: (m.medicationCodeableConcept && m.medicationCodeableConcept.text)
            || (m.medicationCodeableConcept && m.medicationCodeableConcept.coding && m.medicationCodeableConcept.coding[0] && m.medicationCodeableConcept.coding[0].display) || 'Medication',
          freqGrid: grid,
          dose: grid ? [grid, dur].filter(Boolean).join(' · ') : text,
        };
      }),
  );
  if (meds.length) out.medications = meds;

  // 4) Investigations — the encounter's ServiceRequest lab orders ({ name }).
  const invs = (serviceRequests || [])
    .map((sr) => (sr && sr.code && sr.code.text) || (sr && sr.code && sr.code.coding && sr.code.coding[0] && sr.code.coding[0].display) || '')
    .filter(Boolean)
    .map((name) => ({ name }));
  if (invs.length) out.investigations = invs;

  return out;
}

// ── the whole Visit list (state-machine.mjs:1413-1522 listPatientVisitHistory) ──
// `graph` = {
//   patientId, excludeEncounterId,
//   encounters: Encounter[] (newest-first),
//   childrenByEncounter: Map<encId, { compositions, conditions, medicationRequests,
//     serviceRequests, questionnaireResponses, documentReferences }>
// }  — medplum-history.js assembles it from ONE patient-wide graph search and
// calls this. Pure: no I/O.
export function shapePatientHistory(graph) {
  const encounters = (graph && graph.encounters) || [];
  const childrenByEncounter = (graph && graph.childrenByEncounter) || new Map();
  const excludeEncounterId = (graph && graph.excludeEncounterId) || null;

  const encs = encounters.filter((e) => e.id !== excludeEncounterId);
  // The doctor currently viewing — the excluded (current) encounter's doctor if
  // one is given, else the most-recent encounter's. Used only to flag prior
  // visits that belonged to a DIFFERENT doctor.
  const currentEnc = (excludeEncounterId && encounters.find((e) => e.id === excludeEncounterId)) || encounters[0] || null;
  const currentDoctorId = currentEnc ? doctorForEncounter(currentEnc).doctorId : null;

  const out = [];
  for (const enc of encs) {
    const kids = childrenByEncounter.get(enc.id) || {};
    const comps = kids.compositions || [];
    const conditions = kids.conditions || [];
    const medRequests = kids.medicationRequests || [];
    const serviceRequests = kids.serviceRequests || [];
    const questionnaireResponses = kids.questionnaireResponses || [];
    const docs = kids.documentReferences || [];

    const intakeComp = freshestByKind(comps, 'intake-summary');
    // Prefer the SIGNED visit-summary's final draft over the still-mutable
    // working 'prescription'/'consult-draft' draft — exactly what the doctor
    // signed, never an over-stuffed working draft.
    const signedComp = freshestByKind(comps, 'visit-summary');
    const rxComp = freshestByKind(comps, 'prescription') || freshestByKind(comps, 'consult-draft');
    const draft = (Object.keys(draftFromComposition(signedComp)).length ? draftFromComposition(signedComp) : draftFromComposition(rxComp)) || {};

    let medications = Array.isArray(draft.medications)
      ? draft.medications.map((m) => {
          const strength = m.strength || m.dose || '';
          const grid = freqToGrid(m.frequency);
          return {
            name: m.name || m.medication || 'Medication',
            strength,
            frequency: m.frequency || '',
            freqGrid: grid,
            duration: m.duration || '',
            dose: [strength, grid || m.frequency, m.duration].filter(Boolean).join(' · '),
          };
        })
      : [];
    medications = dedupeMeds(medications);

    // App-draft card fields (source of truth for an app-authored consult; empty
    // for a visit whose draft lives only in FHIR children).
    let chiefComplaint = chiefComplaintOf(intakeComp);
    let diagnosis = draft.diagnosis || null;
    let icd10 = draft.icd10 || null;
    let investigations = Array.isArray(draft.investigations) ? draft.investigations : [];

    // Fill any empty card field from the encounter's real FHIR children —
    // ADDITIVE, never overrides a real app-authored value.
    if (!chiefComplaint || !diagnosis || !medications.length || !investigations.length) {
      const fhir = deriveVisitSummaryFromFhir(enc, {
        compositions: comps, conditions, medRequests, questionnaireResponses, serviceRequests,
      });
      if (!chiefComplaint && fhir.chiefComplaint) chiefComplaint = fhir.chiefComplaint;
      if (!diagnosis && fhir.diagnosis) { diagnosis = fhir.diagnosis; if (!icd10 && fhir.icd10) icd10 = fhir.icd10; }
      if (!medications.length && Array.isArray(fhir.medications)) medications = fhir.medications;
      if (!investigations.length && Array.isArray(fhir.investigations)) investigations = fhir.investigations;
    }

    const reports = docs.map(reportFromDocRef);
    const externalReports = reports.filter((r) => r.source === 'external').length;
    const { doctorId, doctor } = doctorForEncounter(enc);
    // A visit is "external" only when every report in it is external and the
    // encounter has no clinical authorship here (purely a holder for brought-in
    // records).
    const visitExternal = reports.length > 0
      && externalReports === reports.length
      && !doctor && !intakeComp && !rxComp && !signedComp;

    out.push({
      encounterId: enc.id,
      date: (enc.period && enc.period.start)
        || (enc._healther && enc._healther.appointmentSnapshot && enc._healther.appointmentSnapshot.start)
        || (enc.meta && enc.meta.lastUpdated)
        || null,
      doctor,
      doctorId,
      department: departmentForEncounter(enc),
      source: visitExternal ? 'external' : 'this-hospital',
      isOtherDoctor: !!(doctorId && currentDoctorId && doctorId !== currentDoctorId),
      state: stateOf(enc),
      encounterClass: classCodeOf(enc),
      chiefComplaint,
      diagnosis,
      icd10,
      medications,
      investigations,
      followup: draft.followup || null,
      reports,
      reportCounts: { total: reports.length, external: externalReports, thisHospital: reports.length - externalReports },
      // Referral provenance — referrals.mjs flags the TARGET encounter with
      // _healther.referral (disk shadow). No FHIR home in this graph yet, so it's
      // null for pure-FHIR rows — the drawer's referral chip just doesn't show.
      referral: (enc._healther && enc._healther.referral) ? {
        from: enc._healther.referral.fromDepartment || '',
        fromDoctor: enc._healther.referral.fromDoctor || '',
        priority: enc._healther.referral.priority || 'routine',
      } : null,
    });
  }
  return out;
}
